import { isInternalStaff } from "../../../services/lookupService";
import { getApiTable, pickApiRowValue, getStaffTrainingAdditionalRolesFromResponse } from "./qmsApiUtils";
import {
  getVaultDocumentLibraryStaffList,
  getVaultDocumentLibraryUsers,
  getVaultSavedMyLearningUsers,
  getVaultStaffList,
} from "./qmsVaultWorkspaceUtils";

function isAssignedFlag(value) {
  return value === true || value === 1 || value === "1";
}

function filterMyLearningEligibleStaff(rows = []) {
  return (rows || []).filter((row) => isInternalStaff(row));
}

/** Normalize vault staff rows for QmsDocumentLibraryStaffPanel. */
export function mapPublishStaffPickerRow(row, assigned = false) {
  return {
    StaffId: row.StaffId,
    StaffName: row.StaffName || row.UserName || "—",
    RoleId: row.RoleId || "",
    Roles: row.Roles || "—",
    IsCRO: row.IsCRO ?? row.IsCro ?? 0,
    Assigned: assigned ? 1 : 0,
  };
}

export function getStaffMyLearningRoleIds(staffRow, additionalRoleRows = []) {
  const additionalByStaff = buildAdditionalTrainingRolesByStaff(additionalRoleRows);
  return [...getStaffRoleIds(staffRow, additionalByStaff)];
}

/** Map Document Library My Learning picker rows for assignment panel. */
export function mapMyLearningLibraryStaffRows(rows = []) {
  return (rows || []).map((row) =>
    mapPublishStaffPickerRow(
      {
        StaffId: pickApiRowValue(row, "StaffId", "staffId"),
        StaffName:
          pickApiRowValue(row, "StaffName", "UserName", "staffName", "userName") || "—",
        RoleId: pickApiRowValue(row, "RoleId", "RoleIdRef", "roleId") || "",
        Roles: pickApiRowValue(row, "Roles", "RoleName", "roles") || "—",
      },
      Number(row.Assigned) === 1,
    ),
  );
}

export function buildMyLearningPublishStaffRows(workspaceResponse) {
  const pool = filterMyLearningEligibleStaff(getVaultStaffList(workspaceResponse));
  const saved = filterMyLearningEligibleStaff(getVaultSavedMyLearningUsers(workspaceResponse));
  const assignedIds = new Set(saved.map((row) => String(row.StaffId || "")));

  if (pool.length) {
    return pool.map((row) =>
      mapPublishStaffPickerRow(row, assignedIds.has(String(row.StaffId || ""))),
    );
  }

  return saved.map((row) => mapPublishStaffPickerRow(row, true));
}

export function buildLibraryPublishStaffRows(workspaceResponse) {
  const pool = filterMyLearningEligibleStaff(getVaultDocumentLibraryStaffList(workspaceResponse));
  const saved = filterMyLearningEligibleStaff(getVaultDocumentLibraryUsers(workspaceResponse));
  const assignedIds = new Set(
    saved
      .filter((row) => isAssignedFlag(row.IsActive))
      .map((row) => String(row.StaffId || "")),
  );

  if (pool.length) {
    return pool.map((row) =>
      mapPublishStaffPickerRow(row, assignedIds.has(String(row.StaffId || ""))),
    );
  }

  return saved
    .filter((row) => isAssignedFlag(row.IsActive))
    .map((row) => mapPublishStaffPickerRow(row, true));
}

export function getDocumentLibraryEligibleStaffIdSet(staffRows = []) {
  return new Set((staffRows || []).map((row) => String(row.StaffId)));
}

export function countAssignedStaffRows(rows) {
  return (rows || []).filter((row) => Number(row.Assigned) === 1).length;
}

/** Normalize StaffId / RoleId for comparisons (case, braces). */
export function normalizeAssignmentId(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[{}]/g, "");
}

function buildAdditionalTrainingRolesByStaff(additionalRoleRows = []) {
  const map = new Map();
  (additionalRoleRows || []).forEach((row) => {
    const staffId = normalizeAssignmentId(row.StaffId || row.staffId);
    const roleId = normalizeAssignmentId(row.RoleId || row.RoleIdRef || row.roleId);
    if (!staffId || !roleId) return;
    if (!map.has(staffId)) map.set(staffId, new Map());
    map.get(staffId).set(roleId, String(row.Roles || row.roles || "").trim() || "Role");
  });
  return map;
}

function resolveRoleName(roleId, staffRow, additionalByStaff) {
  const normalizedRoleId = normalizeAssignmentId(roleId);
  const normalizedStaffId = normalizeAssignmentId(staffRow?.StaffId);
  const normalizedPrimaryRoleId = normalizeAssignmentId(staffRow?.RoleId);

  if (normalizedPrimaryRoleId && normalizedPrimaryRoleId === normalizedRoleId) {
    return String(staffRow?.Roles || "").trim() || "Role";
  }

  return additionalByStaff.get(normalizedStaffId)?.get(normalizedRoleId) || "Role";
}

function getStaffRoleIds(staffRow, additionalByStaff) {
  const staffId = normalizeAssignmentId(staffRow?.StaffId);
  const roleIds = new Set();
  const primaryRoleId = normalizeAssignmentId(staffRow?.RoleId);
  if (primaryRoleId) roleIds.add(primaryRoleId);
  (additionalByStaff.get(staffId)?.keys() || []).forEach((roleId) => roleIds.add(String(roleId)));
  return roleIds;
}

export function staffMatchesMyLearningRole(staffRow, roleId, additionalRoleRows = []) {
  const additionalByStaff =
    additionalRoleRows instanceof Map
      ? additionalRoleRows
      : buildAdditionalTrainingRolesByStaff(additionalRoleRows);
  return getStaffRoleIds(staffRow, additionalByStaff).has(normalizeAssignmentId(roleId));
}

export function getStaffAdditionalTrainingRoleNames(staffRow, additionalRoleRows = []) {
  const additionalByStaff = buildAdditionalTrainingRolesByStaff(additionalRoleRows);
  const staffId = normalizeAssignmentId(staffRow?.StaffId);
  const roles = additionalByStaff.get(staffId);
  if (!roles) return [];
  return [...roles.values()].filter(Boolean);
}

function addStaffToMyLearningRoleGroup(groups, staffRow, roleId, additionalByStaff) {
  const staffId = String(staffRow?.StaffId || "");
  const normalizedStaffId = normalizeAssignmentId(staffId);
  const normalizedPrimaryRoleId = normalizeAssignmentId(staffRow?.RoleId);
  const normalizedRoleId = normalizeAssignmentId(roleId);
  if (!normalizedRoleId || !staffId) return;

  if (!groups.has(normalizedRoleId)) {
    groups.set(normalizedRoleId, {
      roleId: normalizedRoleId,
      roleName: resolveRoleName(normalizedRoleId, staffRow, additionalByStaff),
      staff: new Map(),
    });
  }

  const isTrainingRoleMembership =
    normalizedPrimaryRoleId !== normalizedRoleId &&
    Boolean(additionalByStaff.get(normalizedStaffId)?.has(normalizedRoleId));

  groups.get(normalizedRoleId).staff.set(staffId, {
    ...staffRow,
    RoleId: normalizedRoleId,
    Roles: resolveRoleName(normalizedRoleId, staffRow, additionalByStaff),
    PrimaryRoles: staffRow.Roles || staffRow.RoleName || "—",
    isTrainingRoleMembership,
  });
}

/** Group staff under each org role (primary + Additional Training Roles from user profile). */
export function buildMyLearningRoleGroups(staffRows = [], additionalRoleRows = []) {
  const additionalByStaff = buildAdditionalTrainingRolesByStaff(additionalRoleRows);
  const groups = new Map();
  const staffById = new Map();

  (staffRows || []).forEach((row) => {
    const normalizedStaffId = normalizeAssignmentId(row?.StaffId);
    if (normalizedStaffId) staffById.set(normalizedStaffId, row);

    getStaffRoleIds(row, additionalByStaff).forEach((roleId) => {
      addStaffToMyLearningRoleGroup(groups, row, roleId, additionalByStaff);
    });
  });

  (additionalRoleRows || []).forEach((extraRow) => {
    const normalizedStaffId = normalizeAssignmentId(
      pickApiRowValue(extraRow, "StaffId", "staffId"),
    );
    const normalizedRoleId = normalizeAssignmentId(
      pickApiRowValue(extraRow, "RoleId", "RoleIdRef", "roleId"),
    );
    if (!normalizedStaffId || !normalizedRoleId) return;

    const staffRow = staffById.get(normalizedStaffId);
    if (!staffRow) return;

    addStaffToMyLearningRoleGroup(groups, staffRow, normalizedRoleId, additionalByStaff);
  });

  return Array.from(groups.values())
    .map((group) => ({
      roleId: group.roleId,
      roleName: group.roleName,
      staff: Array.from(group.staff.values()),
    }))
    .sort((left, right) => left.roleName.localeCompare(right.roleName));
}

export const QMS_TRAINING_ROLE_LABEL = "QMS training Role";
export const QMS_TRAINING_ROLE_SUBTITLE = "[QMS training Role]";

export function mapStaffTrainingAdditionalRoleRows(rows = []) {
  return (rows || [])
    .map((row) => ({
      StaffId: pickApiRowValue(row, "StaffId", "staffId"),
      RoleId: pickApiRowValue(row, "RoleId", "RoleIdRef", "roleId"),
      Roles: pickApiRowValue(row, "Roles", "roles") || "",
    }))
    .filter((row) => row.StaffId && row.RoleId);
}

export function parseMyLearningStaffPickerResponse(response) {
  const staffRows = filterMyLearningEligibleStaff(getApiTable(response, "MyLearningUsers"));

  return {
    staff: mapMyLearningLibraryStaffRows(staffRows),
    trainingRoles: getStaffTrainingAdditionalRolesFromResponse(response),
  };
}

export function getRoleAssignmentState(roleStaff = []) {
  if (!roleStaff.length) {
    return { checked: false, indeterminate: false, assignedCount: 0 };
  }

  const assignedCount = roleStaff.filter((row) => Number(row.Assigned) === 1).length;

  return {
    assignedCount,
    checked: assignedCount === roleStaff.length,
    indeterminate: assignedCount > 0 && assignedCount < roleStaff.length,
  };
}

export function sortRoleGroupsAssignedFirst(roleGroups = []) {
  return [...roleGroups].sort((left, right) => {
    const leftState = getRoleAssignmentState(left.staff);
    const rightState = getRoleAssignmentState(right.staff);
    const leftRank = leftState.checked ? 0 : leftState.indeterminate ? 1 : 2;
    const rightRank = rightState.checked ? 0 : rightState.indeterminate ? 1 : 2;

    if (leftRank !== rightRank) return leftRank - rightRank;
    return String(left.roleName || "").localeCompare(String(right.roleName || ""));
  });
}

export function sortStaffAssignedFirst(staffRows = []) {
  return [...staffRows].sort((left, right) => {
    const leftAssigned = Number(left.Assigned) === 1 ? 0 : 1;
    const rightAssigned = Number(right.Assigned) === 1 ? 0 : 1;
    if (leftAssigned !== rightAssigned) return leftAssigned - rightAssigned;
    return String(left.StaffName || "").localeCompare(String(right.StaffName || ""));
  });
}

export function getAssignedStaffIdSet(staffRows = []) {
  return new Set(
    (staffRows || [])
      .filter((row) => Number(row.Assigned) === 1)
      .map((row) => String(row.StaffId)),
  );
}

export function applyAssignedIdSetToStaffRows(staffRows = [], assignedIds) {
  const idSet = assignedIds instanceof Set ? assignedIds : new Set(assignedIds || []);
  return (staffRows || []).map((row) =>
    mapPublishStaffPickerRow(row, idSet.has(String(row.StaffId))),
  );
}

export function getMyLearningEligibleStaffIdSet(staffRows = []) {
  return new Set(
    filterMyLearningEligibleStaff(staffRows).map((row) => String(row.StaffId)),
  );
}

export function areAssignedIdSetsEqual(left, right) {
  const a = left instanceof Set ? left : new Set(left || []);
  const b = right instanceof Set ? right : new Set(right || []);
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

export function buildOrgRoleOptions(staffRows = [], additionalRoleRows = []) {
  return buildMyLearningRoleGroups(staffRows, additionalRoleRows).map((group) => ({
    value: group.roleId,
    label: group.roleName,
  }));
}

export function computeMyLearningAssignedIds(
  staffRows = [],
  selectedRoleIds,
  individualStaffIds,
  excludedStaffIds,
  additionalRoleRows = [],
) {
  const roles =
    selectedRoleIds instanceof Set ? selectedRoleIds : new Set(selectedRoleIds || []);
  const individuals =
    individualStaffIds instanceof Set
      ? individualStaffIds
      : new Set(individualStaffIds || []);
  const excluded =
    excludedStaffIds instanceof Set ? excludedStaffIds : new Set(excludedStaffIds || []);
  const additionalByStaff = buildAdditionalTrainingRolesByStaff(additionalRoleRows);
  const assigned = new Set();

  (staffRows || []).forEach((row) => {
    const staffId = String(row.StaffId || "");
    if (!staffId || excluded.has(staffId)) return;

    if (individuals.has(staffId)) {
      assigned.add(staffId);
      return;
    }

    for (const roleId of roles) {
      if (staffMatchesMyLearningRole(row, roleId, additionalByStaff)) {
        assigned.add(staffId);
        break;
      }
    }
  });

  return assigned;
}

/** UI-only draft from saved staff rows (no per-document role metadata in DB). */
export function initMyLearningAssignmentDraft(staffRows = [], additionalRoleRows = []) {
  const assignedIds = getAssignedStaffIdSet(staffRows);
  const roleGroups = buildMyLearningRoleGroups(staffRows, additionalRoleRows);
  const selectedRoleIds = new Set();
  const individualStaffIds = new Set();
  const excludedStaffIds = new Set();

  roleGroups.forEach((group) => {
    const memberIds = (group.staff || []).map((row) => String(row.StaffId || "")).filter(Boolean);
    if (memberIds.length && memberIds.every((id) => assignedIds.has(id))) {
      selectedRoleIds.add(String(group.roleId));
    }
  });

  assignedIds.forEach((staffId) => {
    const coveredByRole = roleGroups.some((group) => {
      if (!selectedRoleIds.has(String(group.roleId))) return false;
      return (group.staff || []).some((row) => String(row.StaffId) === staffId);
    });
    if (!coveredByRole) individualStaffIds.add(staffId);
  });

  return {
    selectedRoleIds,
    individualStaffIds,
    excludedStaffIds,
  };
}

/** Final staff IDs for existing SaveMyLearningStaff / MyLearningAccess. */
export function serializeMyLearningAssignmentPayload(
  draft,
  staffRows = [],
  additionalRoleRows = [],
) {
  const assignedIds = computeMyLearningAssignedIds(
    staffRows,
    draft?.selectedRoleIds,
    draft?.individualStaffIds,
    draft?.excludedStaffIds,
    additionalRoleRows,
  );

  return {
    staffIds: [...assignedIds]
      .map((id) => String(id).trim())
      .filter(Boolean)
      .join(","),
  };
}

export function getStaffAssignmentSourceLabel(staffRow, draft, additionalRoleRows = []) {
  const staffId = String(staffRow?.StaffId || "");
  if (!staffId) return "";

  const roles =
    draft?.selectedRoleIds instanceof Set
      ? draft.selectedRoleIds
      : new Set(draft?.selectedRoleIds || []);
  const individuals =
    draft?.individualStaffIds instanceof Set
      ? draft.individualStaffIds
      : new Set(draft?.individualStaffIds || []);
  const additionalByStaff = buildAdditionalTrainingRolesByStaff(additionalRoleRows);

  if (individuals.has(staffId)) return "Added individually";

  for (const roleId of roles) {
    if (staffMatchesMyLearningRole(staffRow, roleId, additionalByStaff)) {
      const normalizedStaffId = normalizeAssignmentId(staffRow?.StaffId);
      const normalizedPrimaryRoleId = normalizeAssignmentId(staffRow?.RoleId);
      const normalizedRoleId = normalizeAssignmentId(roleId);
      const viaTrainingRole =
        normalizedPrimaryRoleId !== normalizedRoleId &&
        additionalByStaff.get(normalizedStaffId)?.has(normalizedRoleId);
      if (viaTrainingRole) {
        return QMS_TRAINING_ROLE_SUBTITLE;
      }
      return `Via role: ${resolveRoleName(roleId, staffRow, additionalByStaff)}`;
    }
  }

  return "";
}

export const MY_LEARNING_UNSAVED_MESSAGE =
  "You have unsaved My Learning changes. If you leave now, your changes will be lost.";

export const MY_LEARNING_UNSAVED_ALERT = {
  title: "Unsaved changes",
  message: MY_LEARNING_UNSAVED_MESSAGE,
  type: "warning",
  buttonText: "Leave anyway",
  cancelText: "Stay on page",
};

export function promptDiscardMyLearningChanges(showAlert, onLeave) {
  showAlert({
    ...MY_LEARNING_UNSAVED_ALERT,
    onConfirm: onLeave,
  });
}
